<script setup lang="ts">
import { computed, inject, onBeforeMount, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { SET_REQUEST_LOADING } from '@/store/mutation-types'
import DashboardLegalModal from '@/components/dashboard/modals/DashboardLegalModal.vue'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import SignupSteps from '@/components/views/manage-account/SignupSteps.vue'
import swiftaidLogo from '@/assets/images/main/logo.svg'

const store = useStore()
const route = useRoute()
const rootSiteDomain = inject<string>(
  'rootSiteDomain',
  'https://www.swiftaid.co.uk'
)

const currentYear = ref(new Date().getFullYear())
const termsLink = import.meta.env.VITE_LEGAL_TERMS_LINK
const privacyLink = import.meta.env.VITE_LEGAL_PRIVACY_LINK
const contactSupportUrl = `${rootSiteDomain}/donors/manage-account/something-else/`

const requestLoading = computed(() => store.state.requestLoading)

/** Signup chrome: /signup plus create details / authorise. */
const showSignupSteps = computed(() => {
  const name = String(route.name || '')
  return (
    name === 'Signup' ||
    name === 'SignupMagicLink' ||
    name === 'DONOR_FLOW_ManageAccountCreateDetails' ||
    name === 'DONOR_FLOW_ManageAccountCreateDeclaration'
  )
})

const signupStep = computed((): 1 | 2 | 3 => {
  const name = String(route.name || '')
  if (name === 'DONOR_FLOW_ManageAccountCreateDeclaration') return 3
  if (name === 'DONOR_FLOW_ManageAccountCreateDetails') return 2
  // Email OTP / magic-link confirm stays under the Email step.
  return 1
})

const setTermsModalVisibility = (visibility: boolean) => {
  store.commit('SET_TERMS_MODAL_VISIBILITY', visibility)
}

const setPrivacyModalVisibility = (visibility: boolean) => {
  store.commit('SET_PRIVACY_MODAL_VISIBILITY', visibility)
}

onBeforeMount(() => {
  // Reuse BobV1 dashboard tokens and components without the sidebar shell.
  document.body.className = 'dashboard-layout'
  store.commit(SET_REQUEST_LOADING, false)
})

onUnmounted(() => {
  // DashboardLayout will re-apply its own body class when entering the app shell.
})
</script>

<template>
  <div class="sa-auth">
    <a class="sa-sr-only" href="#sa-auth-main">Skip to main content</a>

    <header class="sa-auth__top sa-auth__top--brand-only">
      <a
        class="sa-auth__brand"
        :href="rootSiteDomain"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          :src="swiftaidLogo"
          class="sa-auth__logo"
          alt="Swiftaid"
          width="180"
          height="32"
        />
      </a>
    </header>

    <main id="sa-auth-main" class="sa-auth__main" role="main">
      <div class="sa-auth__stage">
        <SignupSteps
          v-if="showSignupSteps"
          :current-step="signupStep"
          class="sa-auth__steps-enter"
        />

        <div
          class="sa-auth__panel sa-auth__panel--enter"
          :class="{ 'sa-auth__panel--wide': route.meta.authPanelWide }"
        >
          <!-- Keep router-view mounted during requestLoading. Replacing it with a
               spinner unmounted Agree mid-flight (esp. with mock API latency) so
               success never navigated and the authorise step remounted in a loop. -->
          <router-view />
        </div>
      </div>

      <div
        v-if="requestLoading"
        class="sa-auth__loader sa-auth__loader--overlay"
        role="status"
        aria-live="polite"
        :aria-label="$t('Accessibility.Button.Loading')"
      >
        <SaSpinner size="page" />
      </div>
    </main>

    <footer class="sa-auth__footer sa-auth__footer--enter">
      <ul class="sa-auth__legal">
        <li>
          <a
            :href="termsLink"
            class="sa-link"
            data-testid="legal-terms-link"
            @click.prevent="setTermsModalVisibility(true)"
          >
            {{ $t('LoginRegister.Footer.Terms') }}
          </a>
        </li>
        <li>
          <a
            :href="privacyLink"
            class="sa-link"
            data-testid="legal-privacy-link"
            @click.prevent="setPrivacyModalVisibility(true)"
          >
            {{ $t('LoginRegister.Footer.Privacy') }}
          </a>
        </li>
        <li>
          <a
            :href="contactSupportUrl"
            class="sa-link"
            data-testid="contact-support-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('LoginRegister.Footer.ContactSupport') }}
          </a>
        </li>
      </ul>
      <p class="sa-auth__copyright">
        &copy; {{ currentYear }}
        <a href="https://streeva.com" class="sa-link" rel="noopener noreferrer">
          {{ $t('LoginRegister.Footer.Streeva') }}
        </a>
        . {{ $t('LoginRegister.Footer.Statement') }}
      </p>
    </footer>

    <DashboardLegalModal />
  </div>
</template>
